import { CustomizerEngine, HtmlTransformer, SerializedLayer, TextLayer } from 'smg/customizer';
import { BuilderTextDropUI } from 'smg/customizer/text-drop';
import { FONT_NAMES, FontHasFace, ResolveFontFace, ResolveSerializedTextStyle } from '../product-customizer/fonts';

export interface TextDropState {
    front: SerializedLayer[];
    back: SerializedLayer[];
}

type coinSide = 'front' | 'back';

// Printable-area clip radii — must match the image mask + background color radii
const CLIP_RADIUS_WITH_RING = 322;
const CLIP_RADIUS_NO_RING = 408;

/**
 * Free-position text blocks ("text drop") on the coin builder — the text counterpart to
 * ImageUploadUI. The shared BuilderTextDropUI core owns blocks/selection/actions; this
 * subclass owns the two-engine (front/back) lifecycle, the ring-dependent printable
 * clip, and the persistence state for CoinDesignPersistence. Blocks are dynamic layers,
 * so like images they're rebuilt from extra state on restore rather than through
 * engine.deserialize.
 */
export class TextDropUI extends BuilderTextDropUI<coinSide> {
    #frontEngine: CustomizerEngine;
    #backEngine: CustomizerEngine;
    #frontTransformer: HtmlTransformer;
    #backTransformer: HtmlTransformer;
    #currentSide: coinSide = 'front';

    constructor(frontEngine: CustomizerEngine, backEngine: CustomizerEngine) {
        super({
            fonts: FONT_NAMES,
            resolveFontFace: ResolveFontFace,
            fontHasFace: FontHasFace,
            changedEventName: 'coin-text-drop-changed',
            protectedSelectors:
                '#front-canvas, #back-canvas, #coin-preview-container, ' +
                '.customizer-text-toolbar, .customizer-bend-handle, .html-transformer-overlay, ' +
                '#design-uploads, #lg-design-uploads, .design-layer-row, .layer-action-bar, .add-text-drop-btn, .add-text-btn',
            duplicateOffsetPx: 60,
        });
        this.#frontEngine = frontEngine;
        this.#backEngine = backEngine;

        this.#frontTransformer = new HtmlTransformer(frontEngine);
        this.#backTransformer = new HtmlTransformer(backEngine);
        this.#frontTransformer.hide();
        this.#backTransformer.hide();

        this.hookEngine(frontEngine);
        this.hookEngine(backEngine);

        // The hidden side radios are the coin page's source of truth for side switching
        document.querySelectorAll<HTMLInputElement>('input[name="front-back-radio"]').forEach($radio => {
            $radio.addEventListener('change', () => {
                if ($radio.checked) {
                    this.#currentSide = $radio.value === 'back' ? 'back' : 'front';
                    this.deselectAll();
                }
            });
        });

        // The clip circle tracks the text ring like image masks do (same state holder
        // BackgroundColorUI syncs from)
        document.getElementById('wants-text-ring-switch')?.addEventListener('change', () => {
            for (const { layer, tag } of this.blockMap.values()) {
                this.applyClip(layer, tag);
            }
        });
    }

    protected override currentTag(): coinSide {
        return this.#currentSide;
    }

    protected override engineFor(side: coinSide): CustomizerEngine {
        return side === 'front' ? this.#frontEngine : this.#backEngine;
    }

    protected override transformerFor(side: coinSide): HtmlTransformer {
        return side === 'front' ? this.#frontTransformer : this.#backTransformer;
    }

    protected override allTransformers(): HtmlTransformer[] {
        return [this.#frontTransformer, this.#backTransformer];
    }

    // Ink is ink at print time: text must crop to the coin's printable circle exactly
    // like uploaded images (their alpha masks use these same radii)
    protected override applyClip(layer: TextLayer, side: coinSide): void {
        const $ringToggle = document.getElementById('wants-text-ring-switch') as HTMLInputElement | null;
        const radius = $ringToggle?.checked ? CLIP_RADIUS_WITH_RING : CLIP_RADIUS_NO_RING;

        const stage = this.engineFor(side).getStage();
        const stageScale = stage.scaleX() || 1;
        const centerX = stage.width() / stageScale / 2;
        const centerY = stage.height() / stageScale / 2;

        layer.setClipFunc(ctx => {
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2, false);
        });
    }

    protected override blockCenter(side: coinSide): { x: number; y: number } {
        const stage = this.engineFor(side).getStage();
        const stageScale = stage.scaleX() || 1;
        return { x: stage.width() / stageScale / 2, y: stage.height() / stageScale / 2 };
    }

    // Spawn above center — the coin page's centered Upload CTA sits exactly at center
    // and would hide a new block
    protected override spawnPosition(side: coinSide): { x: number; y: number } {
        const stage = this.engineFor(side).getStage();
        const stageScale = stage.scaleX() || 1;
        return { x: stage.width() / stageScale / 2, y: stage.height() / stageScale * 0.38 };
    }

    /** Blocks on a side, for the layer list (order unspecified — sort by actual z-index) */
    blocksForSide(side: coinSide): { id: string; layer: TextLayer }[] {
        const rows: { id: string; layer: TextLayer }[] = [];
        for (const [id, block] of this.blockMap) {
            if (block.tag === side) {
                rows.push({ id, layer: block.layer });
            }
        }
        return rows;
    }

    /** Whether a side has any text blocks (drives e.g. the centered Upload CTA) */
    hasBlocks(side: coinSide): boolean {
        for (const block of this.blockMap.values()) {
            if (block.tag === side) {
                return true;
            }
        }
        return false;
    }

    /** Trimmed text of visible, non-empty blocks on a side — what actually prints (also spell-checked) */
    visibleBlockTexts(side: coinSide): string[] {
        const texts: string[] = [];
        for (const block of this.blockMap.values()) {
            if (block.tag !== side || !block.layer.isVisible()) {
                continue;
            }
            const text = block.layer.getText().trim();
            if (text) {
                texts.push(text);
            }
        }
        return texts;
    }

    /** Whether a side has printable text content (visible blocks with actual text) */
    hasVisibleContent(side: coinSide): boolean {
        return this.visibleBlockTexts(side).length > 0;
    }

    /** Serialized blocks per side, stored in the design's extra state */
    getState(): TextDropState {
        const state: TextDropState = { front: [], back: [] };
        for (const { layer, tag } of this.blockMap.values()) {
            state[tag].push(layer.serialize());
        }
        return state;
    }

    /** Any block with actual text counts as design content */
    hasContent(): boolean {
        for (const { layer } of this.blockMap.values()) {
            if (layer.getText().trim().length > 0) {
                return true;
            }
        }
        return false;
    }

    /** Rebuild blocks from saved state (restore path — blocks are dynamic layers) */
    async restoreState(state: TextDropState): Promise<void> {
        this.deselectAll();
        for (const id of [...this.blockMap.keys()]) {
            this.performDelete(id);
        }

        for (const side of ['front', 'back'] as const) {
            // Ascending saved z-order: each block's attachTo re-applies its saved index,
            // which lands correctly only if the blocks below it already exist
            const sorted = [...state[side] ?? []].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
            // Legacy saves may name retired device fonts — swap in the catalog faces.
            // Resolved in parallel (attach order below must stay sequential for z-order)
            const resolved = await Promise.all(sorted.map(saved => ResolveSerializedTextStyle(saved)));
            for (const serialized of resolved) {
                // Deserialize into the fields BEFORE attaching: addLayer autosaves via
                // significantChange, so the block must be registered with its real state
                const layer = new TextLayer(serialized.id);
                await layer.deserialize(serialized);
                this.blockMap.set(serialized.id, { layer, tag: side });
                await this.engineFor(side).addLayer(serialized.id, layer);
                this.applyClip(layer, side);
                this.wireSelection(serialized.id);
                this.seedCounterFromId(serialized.id);
            }
        }
        this.notifyChanged();
    }
}
